<template>
  <div class="segment-config">
    <div class="page-header">
      <div class="header-left">
        <h3>环节配置管理</h3>
        <el-select v-model="selectedFormKey" placeholder="请选择问卷" size="small" style="width: 250px; margin-left: 16px" @change="onFormChange">
          <el-option v-for="form in formList" :key="getFormKey(form)" :label="stripHtml(form.name)" :value="getFormKey(form)" />
        </el-select>
      </div>
      <div class="header-actions" v-if="selectedFormKey">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="addRootSegment">新增一级环节</el-button>
        <el-button size="small" @click="expandAll">全部展开</el-button>
        <el-button size="small" @click="collapseAll">全部折叠</el-button>
      </div>
    </div>

    <div class="segment-tree-container" v-loading="loading">
      <el-tree
        ref="tree"
        :data="segmentTree"
        :props="treeProps"
        node-key="segmentKey"
        default-expand-all
        draggable
        :allow-drop="allowDrop"
        @node-drop="handleDrop"
      >
        <div class="tree-node" slot-scope="{ node, data }">
          <span class="node-icon">{{ data.children && data.children.length > 0 ? '📂' : '📄' }}</span>
          <span class="node-name">{{ data.segmentName }}</span>
          <span class="node-weight" v-if="data.weight">(权重: {{ data.weight }}%)</span>
          <span class="node-count" v-if="data.itemCount" @click.stop="openItemDialog(data)" style="cursor: pointer">(关联题目: {{ data.itemCount }}个)</span>
          <span class="node-actions">
            <el-button type="text" size="mini" @click.stop="openItemDialog(data)">关联题目</el-button>
            <el-button type="text" size="mini" @click.stop="editSegment(data)">编辑</el-button>
            <el-button type="text" size="mini" @click.stop="addChild(data)">+子环节</el-button>
            <el-button type="text" size="mini" style="color: #f56c6c" @click.stop="deleteSegment(data)">删除</el-button>
          </span>
        </div>
      </el-tree>

      <div class="empty-tip" v-if="!loading && segmentTree.length === 0">
        <p>暂无环节配置</p>
        <el-button type="primary" @click="addRootSegment">创建第一个环节</el-button>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px" @close="resetForm">
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="环节名称" prop="segmentName">
          <el-input v-model="form.segmentName" placeholder="请输入环节名称" />
        </el-form-item>
        <el-form-item label="父环节">
          <el-cascader
            v-model="form.parentPath"
            :options="parentOptions"
            :props="{ checkStrictly: true, value: 'segmentKey', label: 'segmentName', children: 'children' }"
            placeholder="无(顶级环节)"
            clearable
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="权重(%)">
          <el-input-number v-model="form.weight" :min="0" :max="100" :precision="1" />
        </el-form-item>
        <el-form-item label="目标分">
          <el-input-number v-model="form.targetScore" :min="0" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sortOrder" :min="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveSegment" :loading="saving">保存</el-button>
      </div>
    </el-dialog>

    <!-- 题目关联弹窗 -->
    <el-dialog 
      :title="'关联题目 - ' + (currentSegment ? currentSegment.segmentName : '')" 
      :visible.sync="itemDialogVisible" 
      width="600px"
      @close="resetItemDialog"
    >
      <div class="item-selection-container" v-loading="loadingItems">
        <div class="search-bar">
          <el-input 
            v-model="itemSearchKeyword" 
            placeholder="搜索题目..." 
            prefix-icon="el-icon-search" 
            size="small"
            clearable
            style="flex: 1"
          />
          <el-checkbox v-model="hideLinkedItems" style="margin-left: 12px; white-space: nowrap;">隐藏已关联</el-checkbox>
        </div>
        <div class="item-list" v-if="filteredFormItems.length > 0">
          <el-checkbox-group v-model="selectedItemKeys">
            <div 
              v-for="item in filteredFormItems" 
              :key="item.itemKey" 
              class="item-row"
            >
              <el-checkbox :label="item.itemKey">
                <span class="item-index">{{ item.sortOrder + 1 }}.</span>
                <span class="item-label" v-html="stripHtml(item.label)"></span>
                <span class="item-type">({{ getItemTypeName(item.itemType) }})</span>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>
        <div class="empty-items" v-else-if="!loadingItems">
          <p>{{ itemSearchKeyword ? '没有匹配的题目' : '该问卷暂无题目' }}</p>
        </div>
      </div>
      <div slot="footer">
        <span class="selected-count">已选择 {{ selectedItemKeys.length }} 个题目</span>
        <el-button @click="itemDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItemSegments" :loading="savingItems">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'index/axios'
import { getProjectForms } from '@/app/index/api/project/manager'

export default {
  name: 'SegmentConfig',
  data() {
    return {
      projectId: null,
      formList: [],
      selectedFormKey: '',
      loading: false,
      saving: false,
      segmentTree: [],
      treeProps: {
        children: 'children',
        label: 'segmentName'
      },
      dialogVisible: false,
      isEdit: false,
      form: {
        id: null,
        segmentKey: '',
        segmentName: '',
        parentPath: [],
        weight: null,
        targetScore: 0,
        sortOrder: 0
      },
      rules: {
        segmentName: [{ required: true, message: '请输入环节名称', trigger: 'blur' }]
      },
      // 题目关联弹窗相关
      itemDialogVisible: false,
      currentSegment: null,
      formItems: [],
      selectedItemKeys: [],
      itemSearchKeyword: '',
      loadingItems: false,
      savingItems: false,
      hideLinkedItems: true,  // 默认隐藏已关联的题目
      allLinkedItemKeys: []   // 所有已被其他环节关联的题目
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑环节' : '新增环节'
    },
    parentOptions() {
      // 返回所有可选的父节点（编辑时排除自身及子节点）
      if (!this.isEdit) return this.segmentTree
      return this.filterSelfAndChildren(this.segmentTree, this.form.segmentKey)
    },
    // 过滤后的题目列表（根据搜索关键词和是否隐藏已关联）
    filteredFormItems() {
      let items = this.formItems
      
      // 根据关键词过滤
      if (this.itemSearchKeyword) {
        const kw = this.itemSearchKeyword.toLowerCase()
        items = items.filter(item => {
          const label = this.stripHtml(item.label || '').toLowerCase()
          return label.includes(kw)
        })
      }
      
      // 隐藏已被其他环节关联的题目（但保留当前环节已关联的，方便取消关联）
      if (this.hideLinkedItems) {
        items = items.filter(item => {
          // 当前环节已关联的题目不隐藏（方便用户取消）
          if (this.selectedItemKeys.includes(item.itemKey)) return true
          // 其他环节已关联的题目隐藏
          return !this.allLinkedItemKeys.includes(item.itemKey)
        })
      }
      
      return items
    }
  },
  mounted() {
    this.projectId = this.$route.params.projectId
    this.loadFormList()
  },
  methods: {
    async loadFormList() {
      try {
        const res = await getProjectForms(this.projectId)
        // 尝试多种数据结构
        let forms = res?.data?.data || res?.data || res || []
        if (!Array.isArray(forms)) forms = []
        this.formList = forms
        if (this.formList.length > 0) {
          // 兼容多种字段名：formKey、key、sourceId
          const firstForm = this.formList[0]
          this.selectedFormKey = firstForm.formKey || firstForm.key || firstForm.sourceId || firstForm.source_id
          this.loadTree()
        }
      } catch (e) {
        console.error('加载问卷列表失败', e)
        this.formList = []
      }
    },
    onFormChange() {
      this.segmentTree = []
      if (this.selectedFormKey) {
        this.loadTree()
      }
    },
    async loadTree() {
      if (!this.selectedFormKey) return
      this.loading = true
      try {
        const res = await axios.post('/Segment/tree', { FormKey: this.selectedFormKey })
        // 处理多层嵌套的响应格式: res.data.data.tree 或 res.data.tree 或 res.tree
        const data = res?.data?.data || res?.data || res || {}
        const tree = data.tree || []
        this.segmentTree = this.normalizeTree(tree)
      } catch (e) {
        console.error('加载环节树失败', e)
        this.segmentTree = []
      } finally {
        this.loading = false
      }
    },
    // 将后端返回的 PascalCase 转换为 camelCase
    normalizeTree(nodes) {
      return nodes.map(node => ({
        id: node.Id || node.id,
        segmentKey: node.SegmentKey || node.segmentKey,
        parentKey: node.ParentKey || node.parentKey,
        segmentName: node.SegmentName || node.segmentName,
        weight: node.Weight || node.weight,
        targetScore: node.TargetScore || node.targetScore,
        level: node.Level || node.level,
        sortOrder: node.SortOrder || node.sortOrder,
        itemCount: node.ItemCount || node.itemCount || 0,
        children: node.Children ? this.normalizeTree(node.Children) : (node.children ? this.normalizeTree(node.children) : [])
      }))
    },
    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '').trim()
    },
    // 获取表单 Key，兼容多种字段命名
    getFormKey(form) {
      return form.formKey || form.key || form.sourceId || form.source_id || ''
    },
    resetForm() {
      this.form = {
        id: null,
        segmentKey: '',
        segmentName: '',
        parentPath: [],
        weight: null,
        targetScore: 0,
        sortOrder: 0
      }
      this.isEdit = false
    },
    addRootSegment() {
      this.resetForm()
      this.dialogVisible = true
    },
    addChild(parent) {
      this.resetForm()
      this.form.parentPath = this.getNodePath(parent.segmentKey)
      this.dialogVisible = true
    },
    editSegment(data) {
      this.isEdit = true
      this.form = {
        id: data.id,
        segmentKey: data.segmentKey,
        segmentName: data.segmentName,
        parentPath: data.parentKey ? this.getNodePath(data.parentKey) : [],
        weight: data.weight,
        targetScore: data.targetScore,
        sortOrder: data.sortOrder
      }
      this.dialogVisible = true
    },
    async deleteSegment(data) {
      try {
        await this.$confirm(`确定要删除"${data.segmentName}"及其所有子环节吗？`, '提示', { type: 'warning' })
        await axios.post('/Segment/delete', { FormKey: this.selectedFormKey, SegmentKey: data.segmentKey })
        this.$message.success('删除成功')
        this.loadTree()
      } catch (e) {
        if (e !== 'cancel') {
          console.error(e)
          this.$message.error('删除失败')
        }
      }
    },
    async saveSegment() {
      try {
        await this.$refs.form.validate()
      } catch { return }

      this.saving = true
      try {
        const parentKey = this.form.parentPath.length > 0 ? this.form.parentPath[this.form.parentPath.length - 1] : null
        await axios.post('/Segment/save', {
          Id: this.form.id,
          FormKey: this.selectedFormKey,
          SegmentKey: this.form.segmentKey,
          ParentKey: parentKey,
          SegmentName: this.form.segmentName,
          Weight: this.form.weight,
          TargetScore: this.form.targetScore,
          SortOrder: this.form.sortOrder
        })
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.loadTree()
      } catch (e) {
        console.error(e)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    expandAll() {
      Object.keys(this.$refs.tree.store.nodesMap).forEach(key => {
        this.$refs.tree.store.nodesMap[key].expanded = true
      })
    },
    collapseAll() {
      Object.keys(this.$refs.tree.store.nodesMap).forEach(key => {
        this.$refs.tree.store.nodesMap[key].expanded = false
      })
    },
    allowDrop(draggingNode, dropNode, type) {
      return type !== 'inner' || dropNode.data.children !== undefined
    },
    async handleDrop(draggingNode, dropNode, type) {
      // TODO: 保存拖拽后的排序
      console.log('Drop:', draggingNode.data.segmentName, '->', dropNode.data.segmentName, type)
    },
    getNodePath(segmentKey) {
      const path = []
      const findPath = (nodes, target, currentPath) => {
        for (const node of nodes) {
          if (node.segmentKey === target) {
            path.push(...currentPath, node.segmentKey)
            return true
          }
          if (node.children && findPath(node.children, target, [...currentPath, node.segmentKey])) {
            return true
          }
        }
        return false
      }
      findPath(this.segmentTree, segmentKey, [])
      return path
    },
    filterSelfAndChildren(nodes, excludeKey) {
      return nodes.filter(n => n.segmentKey !== excludeKey).map(n => ({
        ...n,
        children: n.children ? this.filterSelfAndChildren(n.children, excludeKey) : []
      }))
    },
    
    // ============ 题目关联相关方法 ============
    
    // 打开题目关联弹窗
    async openItemDialog(segment) {
      this.currentSegment = segment
      this.itemDialogVisible = true
      this.loadingItems = true
      
      try {
        // 并行加载题目列表和已关联的题目
        await Promise.all([
          this.loadFormItems(),
          this.loadSegmentItems(segment.segmentKey)
        ])
      } finally {
        this.loadingItems = false
      }
    },
    
    // 加载问卷的所有题目
    async loadFormItems() {
      try {
        // 使用编辑器同款的 GET 接口: /user/form/items/{formKey}
        const res = await axios.get(`/user/form/items/${this.selectedFormKey}`)
        
        // 处理多种可能的响应格式
        let data = []
        if (Array.isArray(res?.data?.data)) {
          data = res.data.data
        } else if (Array.isArray(res?.data)) {
          data = res.data
        } else if (Array.isArray(res)) {
          data = res
        }
        
        this.formItems = data.map((item, idx) => ({
          itemKey: item.itemKey || item.ItemKey || item.item_key,
          label: item.label || item.Label,
          itemType: item.itemType || item.ItemType || item.item_type,
          sortOrder: item.sortOrder != null ? item.sortOrder : (item.SortOrder != null ? item.SortOrder : idx)
        })).sort((a, b) => a.sortOrder - b.sortOrder)
      } catch (e) {
        console.error('加载题目列表失败', e)
        this.formItems = []
      }
    },
    
    // 加载某个环节已关联的题目
    async loadSegmentItems(segmentKey) {
      try {
        // 获取所有题目的环节关联
        const batchRes = await axios.post('/Segment/item/batch', { FormKey: this.selectedFormKey })
        
        // API 返回格式: { data: { data: { itemKey: [segmentKeys] }, errcode: 0 } }
        const innerData = batchRes?.data?.data || batchRes?.data || {}
        const data = innerData?.data || innerData || {}
        
        // data 格式: { itemKey1: [segmentKey1, segmentKey2], itemKey2: [...] }
        const linkedItemKeys = []      // 当前环节关联的题目
        const allLinked = []           // 所有已被关联的题目
        
        for (const [itemKey, segmentKeys] of Object.entries(data)) {
          if (Array.isArray(segmentKeys) && segmentKeys.length > 0) {
            allLinked.push(itemKey)    // 记录所有已关联的题目
            if (segmentKeys.includes(segmentKey)) {
              linkedItemKeys.push(itemKey)  // 记录当前环节关联的题目
            }
          }
        }
        
        this.selectedItemKeys = linkedItemKeys
        this.allLinkedItemKeys = allLinked
      } catch (e) {
        console.error('加载已关联题目失败', e)
        this.selectedItemKeys = []
        this.allLinkedItemKeys = []
      }
    },
    
    // 重置题目关联弹窗
    resetItemDialog() {
      this.currentSegment = null
      this.formItems = []
      this.selectedItemKeys = []
      this.itemSearchKeyword = ''
    },
    
    // 保存题目关联
    async saveItemSegments() {
      if (!this.currentSegment) return
      
      this.savingItems = true
      try {
        const segmentKey = this.currentSegment.segmentKey
        
        // 获取当前问卷所有题目的环节关联
        const batchRes = await axios.post('/Segment/item/batch', { FormKey: this.selectedFormKey })
        
        // 修正数据解析：后端返回 { data: {...}, errcode: 0 }
        // axios 响应又包一层 data，所以是 res.data.data
        const existingData = batchRes?.data?.data || {}
        
        // 需要更新的题目列表
        const itemsToUpdate = new Set([...Object.keys(existingData), ...this.selectedItemKeys])
        
        // 批量更新每个题目的环节关联
        for (const itemKey of itemsToUpdate) {
          // 确保 existingSegments 是数组
          let existingSegments = existingData[itemKey]
          if (!Array.isArray(existingSegments)) {
            existingSegments = []
          }
          const wasLinked = existingSegments.includes(segmentKey)
          const shouldLink = this.selectedItemKeys.includes(itemKey)
          
          // 只有状态改变时才更新
          if (wasLinked !== shouldLink) {
            let newSegments
            if (shouldLink) {
              // 添加关联
              newSegments = [...existingSegments, segmentKey]
            } else {
              // 移除关联
              newSegments = existingSegments.filter(sk => sk !== segmentKey)
            }
            
            await axios.post('/Segment/item/save', {
              FormKey: this.selectedFormKey,
              ItemKey: itemKey,
              SegmentKeys: newSegments
            })
          }
        }
        
        this.$message.success('保存成功')
        this.itemDialogVisible = false
        // 刷新树以更新 itemCount
        this.loadTree()
      } catch (e) {
        console.error('保存题目关联失败', e)
        this.$message.error('保存失败')
      } finally {
        this.savingItems = false
      }
    },
    
    // 获取题型名称
    getItemTypeName(itemType) {
      const typeMap = {
        'RADIO': '单选',
        'CHECKBOX': '多选',
        'INPUT': '文本',
        'TEXTAREA': '多行文本',
        'SELECT': '下拉',
        'RATE': '评分',
        'DATETIME': '日期',
        'FILE_UPLOAD': '文件',
        'IMG_UPLOAD': '图片',
        'IMAGE_SELECT': '图片选择',
        'MATRIX_SELECT': '矩阵',
        'SIGNATURE': '签名'
      }
      return typeMap[itemType] || itemType || '未知'
    }
  }
}
</script>

<style scoped>
.segment-config {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}
.page-header h3 {
  margin: 0;
  font-size: 16px;
}
.header-left {
  display: flex;
  align-items: center;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.segment-tree-container {
  min-height: 300px;
}
.tree-node {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 0;
}
.node-icon {
  margin-right: 8px;
}
.node-name {
  font-weight: 500;
  margin-right: 8px;
}
.node-weight {
  color: #909399;
  font-size: 12px;
  margin-right: 8px;
}
.node-count {
  color: #67c23a;
  font-size: 12px;
}
.node-actions {
  margin-left: auto;
  opacity: 0;
  transition: opacity 0.2s;
}
.tree-node:hover .node-actions {
  opacity: 1;
}
.empty-tip {
  text-align: center;
  padding: 60px 0;
  color: #909399;
}

/* 题目关联弹窗样式 */
.item-selection-container {
  min-height: 300px;
  max-height: 400px;
  overflow-y: auto;
}
.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.item-list {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.item-row {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}
.item-row:last-child {
  border-bottom: none;
}
.item-row:hover {
  background-color: #f5f7fa;
}
.item-row .el-checkbox {
  display: flex;
  align-items: flex-start;
  width: 100%;
}
.item-row .el-checkbox__label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  white-space: normal;
  line-height: 1.5;
}
.item-index {
  color: #909399;
  margin-right: 4px;
  min-width: 24px;
}
.item-label {
  flex: 1;
  color: #303133;
}
.item-type {
  color: #909399;
  font-size: 12px;
  margin-left: 8px;
}
.empty-items {
  text-align: center;
  padding: 60px 0;
  color: #909399;
}
.selected-count {
  color: #67c23a;
  margin-right: 16px;
  font-size: 13px;
}
</style>
